import { Component, OnDestroy, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { MatTooltipModule } from '@angular/material/tooltip';
import { Router } from '@angular/router';
import { Editor, NgxEditorModule, Toolbar } from 'ngx-editor';
import { AlertCardComponent } from '../../../../shared/components/alert-card/alert-card.component';
import { FooterActionComponent } from '../../../../shared/components/footer-action/footer-action.component';
import { HeaderComponent } from '../../../../shared/components/header/header.component';
import { LoadingBoxComponent } from '@shared/components/loading-box/loading-box.component';
import { TipoCampoEnum } from '@shared/enums/compras/tipo-campo.enum';
import { TipoFormatoEnvioEnum } from '@shared/enums/compras/tipo-formato-envio.enum';
import { FuncionalidadeEnum } from '../../../../shared/enums/usuario/funcionalidade.enum';
import { ModuloEnum } from '../../../../shared/enums/usuario/modulo.enum';
import { AlertService, AlertType } from '../../../../shared/services/alert.service';
import { AuthService } from '../../../../shared/services/apis/usuario/auth.service';
import { GetEmpresaService } from '../../../../shared/services/apis/usuario/get-empresa.service';
import { EmpresaStruct } from '../../../../shared/services/structs/usuario/empresa.struct';
import { ConfiguracaoPedidoService } from '../../../../shared/services/apis/compras/configuracao-pedido.service';
import { ConfiguracaoPedidoRequest } from '../../../../shared/services/requests/compras/configuracao-pedido.request';
import { GetConfiguracaoPedidoResponse } from '../../../../shared/services/responses/compras/configuracao-pedido.response';
import { PortalPedidoPreviewComponent } from '../comunicacao-preview/portal-pedido-preview.component';
import { PortalPreviewData } from '../comunicacao-preview/portal-preview.model';

interface PresetColor {
  name: string;
  color: string;
  heroBg: string;
  heroBgEnd: string;
}

interface FormatOption {
  key: string;
  id: TipoFormatoEnvioEnum;
  label: string;
  icon: string;
  hint: string;
}

// Campo (seção) do documento do pedido. Os fixos (fiscais) não podem ser desligados;
// os demais são ativáveis via toggle (bound a um controle do formulário pela `key`).
// idTipoCampo é um placeholder (Texto) exigido pela tabela configuracao_pedido_campo —
// as seções não têm tipo real, o que importa é a `key` e o estado ativo/inativo.
interface DocumentField {
  key: string;
  name: string;
  meta?: string;
  icon: string;
  fixo: boolean;
  idTipoCampo?: TipoCampoEnum;
}

interface TemplateVariable {
  codigo: string;
  descricao: string;
}

@Component({
  selector: 'app-comunicacao-pedido',
  standalone: true,
  imports: [
    ReactiveFormsModule,
    MatButtonModule,
    MatDialogModule,
    MatFormFieldModule,
    MatIconModule,
    MatInputModule,
    MatSlideToggleModule,
    MatTooltipModule,
    NgxEditorModule,
    AlertCardComponent,
    HeaderComponent,
    FooterActionComponent,
    LoadingBoxComponent,
  ],
  templateUrl: './comunicacao-pedido.component.html',
  // Reaproveita o CSS da tela de cotação para manter paridade visual; o css próprio
  // só acrescenta os detalhes de FIXO/ATIVO do documento.
  styleUrls: [
    '../comunicacao-cotacao/comunicacao-cotacao.component.css',
    './comunicacao-pedido.component.css',
  ],
})
export class ComunicacaoPedidoComponent implements OnInit, OnDestroy {
  public moduloEnum = ModuloEnum.Configuracoes;
  public funcionalidadeEnum = FuncionalidadeEnum.Configuracoes_Suprimentos;
  public form: FormGroup;
  public isSaving = false;
  public isLoading = false;
  /** Só com a configuração do servidor aplicada o salvar fica liberado: senão gravaria a tela vazia. */
  public configCarregada = false;
  public logoDataUrl: string | null = null;
  public editorEmail = new Editor();
  public showVariaveisEmail = false;
  public editorInstrucoes = new Editor();
  public showVariaveisInstrucoes = false;
  public textoInstrucoesPadrao = '';
  public emailAssuntoPadrao = '';
  public emailCorpoPadrao = '';

  public toolbar: Toolbar = [
    ['bold', 'italic'],
    ['underline', 'strike'],
    ['ordered_list', 'bullet_list'],
    [{ heading: ['h1', 'h2', 'h3'] }],
    ['text_color', 'background_color'],
    ['align_left', 'align_center', 'align_right', 'align_justify'],
  ];

  public readonly presetColors: PresetColor[] = [
    { name: 'Vermelho ATS', color: '#B73232', heroBg: '#1C1B1F', heroBgEnd: '#2D2A3A' },
    { name: 'Azul corporativo', color: '#1E5DB8', heroBg: '#0D2A4F', heroBgEnd: '#1A3D6E' },
    { name: 'Verde corporativo', color: '#1E8E3E', heroBg: '#0F2D1A', heroBgEnd: '#1C4A2A' },
    { name: 'Roxo', color: '#7A4FBF', heroBg: '#1E1233', heroBgEnd: '#322050' },
    { name: 'Laranja', color: '#C26A00', heroBg: '#2D1B05', heroBgEnd: '#4A2D0E' },
    { name: 'Cinza neutro', color: '#374151', heroBg: '#111827', heroBgEnd: '#1F2937' },
  ];

  public readonly formatOptions: FormatOption[] = [
    { key: 'formatoLinkPortal', id: TipoFormatoEnvioEnum.LinkPortal, label: 'Link para o portal', icon: 'link', hint: 'Link único enviado por email - o fornecedor acompanha o pedido online, sem necessidade de login.' },
    { key: 'formatoPlanilhaExcel', id: TipoFormatoEnvioEnum.PlanilhaExcel, label: 'Planilha (Excel)', icon: 'table_chart', hint: 'Pedido exportado em .xlsx para o fornecedor - ideal para quem não usa o portal.' },
    { key: 'formatoPdfPreencher', id: TipoFormatoEnvioEnum.PdfPreencher, label: 'PDF do pedido', icon: 'picture_as_pdf', hint: 'PDF do pedido enviado como anexo por email.' },
  ];

  // Seções do documento do pedido (conforme layout aprovado). As fixas são exigência
  // fiscal e não podem ser desligadas.
  public readonly documentFields: DocumentField[] = [
    { key: 'dadosEmpresa', name: 'Dados da empresa', meta: 'logo, razão social, CNPJ, endereço', icon: 'apartment', fixo: true },
    { key: 'numeroDataPedido', name: 'Número e data do pedido', icon: 'tag', fixo: true },
    { key: 'dadosFornecedor', name: 'Dados do fornecedor', meta: 'razão social, CNPJ, endereço', icon: 'storefront', fixo: true },
    { key: 'tabelaItens', name: 'Tabela de itens', meta: 'produto, qtd, unidade, preço unitário, tributos, subtotal', icon: 'table_rows', fixo: true },
    { key: 'totais', name: 'Totais', meta: 'subtotal, frete, outras despesas, total geral', icon: 'functions', fixo: true },
    { key: 'campoCondicoesPagamento', name: 'Condições de pagamento e parcelas', icon: 'payments', fixo: false, idTipoCampo: TipoCampoEnum.Texto },
    { key: 'campoPrazoEntrega', name: 'Prazo de entrega e local', icon: 'local_shipping', fixo: false, idTipoCampo: TipoCampoEnum.Texto },
    { key: 'campoObservacoes', name: 'Observações do pedido', icon: 'notes', fixo: false, idTipoCampo: TipoCampoEnum.Texto },
  ];

  public readonly variaveisEmail: TemplateVariable[] = [
    { codigo: '{numero_pedido}', descricao: 'Número do pedido' },
    { codigo: '{empresa}', descricao: 'Nome da empresa compradora' },
    { codigo: '{nome_fornecedor}', descricao: 'Nome do fornecedor' },
    { codigo: '{lista_produtos}', descricao: 'Lista dos produtos do pedido, numerada pela coluna Item (valor do nItemPed)' },
    { codigo: '{valor_total}', descricao: 'Valor total do pedido' },
    { codigo: '{link_pedido}', descricao: 'Link do pedido online' },
    { codigo: '{email_resposta}', descricao: 'E-mail configurado para recebimento das notas' },
  ];

  public readonly variaveisInstrucoes: TemplateVariable[] = [
    { codigo: '{numero_pedido}', descricao: 'Número do pedido' },
    { codigo: '{empresa}', descricao: 'Nome da empresa compradora' },
    { codigo: '{nome_fornecedor}', descricao: 'Nome do fornecedor' },
    { codigo: '{lista_produtos}', descricao: 'Lista dos produtos do pedido, numerada pela coluna Item (valor do nItemPed)' },
    { codigo: '{valor_total}', descricao: 'Valor total do pedido' },
    { codigo: '{link_pedido}', descricao: 'Link do pedido online' },
    { codigo: '{email_resposta}', descricao: 'E-mail configurado para recebimento das notas' },
  ];

  constructor(
    private formBuilder: FormBuilder,
    private router: Router,
    private alertService: AlertService,
    private authService: AuthService,
    private getEmpresaService: GetEmpresaService,
    private configuracaoPedidoService: ConfiguracaoPedidoService,
    private dialog: MatDialog
  ) {
    this.form = this.createForm();
  }

  // Abre o preview do documento como o fornecedor verá, usando o estado ATUAL
  // do formulário (inclusive alterações ainda não salvas).
  public abrirPreview(): void {
    const v = this.form.getRawValue();
    const data: PortalPreviewData = {
      marcaSigla: v.brandShort || 'ATS',
      marcaRodape: v.brandTagline || '',
      urlLogo: this.logoDataUrl,
      corMarca: v.brandColor || '#B73232',
      corHeroInicio: v.heroBg || '#1C1B1F',
      corHeroFim: v.heroBgEnd || '#2D2A3A',
      razaoSocial: v.brandName || '',
      cnpj: v.buyerCnpj || '',
      endereco: v.buyerAddress || '',
      emailContato: v.buyerEmail || '',
      telefoneContato: v.buyerPhone || '',
      urlSite: v.portalSubdomain || '',
      mensagem: v.welcomeMessage || '',
      camposAtivos: this.documentFields.filter(field => this.isFieldActive(field)).map(field => field.key),
    };

    this.dialog.open(PortalPedidoPreviewComponent, {
      width: 'min(920px, 96vw)',
      maxWidth: '96vw',
      autoFocus: false,
      data,
    });
  }

  public ngOnInit(): void {
    this.carregarEmpresa();
    this.carregarConfiguracao();
  }

  private carregarConfiguracao(): void {
    this.isLoading = true;
    this.configuracaoPedidoService.get().subscribe({
      next: response => {
        this.isLoading = false;
        if (response.isError) {
          this.alertService.show('Atenção', response.errorDescription || 'Não foi possível carregar a configuração.', AlertType.warning);
          return;
        }
        this.aplicarConfiguracao(response);
        this.configCarregada = true;
      },
      error: () => {
        this.isLoading = false;
        this.alertService.show('Erro', 'Não foi possível carregar a configuração.', AlertType.error);
      },
    });
  }

  // Popula o formulário a partir da resposta. Formatos e seções vêm como listas
  // (ids / chaves habilitadas) e são convertidos de volta para os toggles da tela.
  private aplicarConfiguracao(response: GetConfiguracaoPedidoResponse): void {
    this.logoDataUrl = response.urlLogo ?? null;

    this.form.patchValue({
      brandShort: response.marcaSigla ?? '',
      brandTagline: response.marcaRodape ?? '',
      brandColor: response.corMarca || '#B73232',
      heroBg: response.corHeroInicio || '#1C1B1F',
      heroBgEnd: response.corHeroFim || '#2D2A3A',
      buyerEmail: response.emailContato ?? '',
      buyerPhone: response.telefoneContato ?? '',
      portalSubdomain: response.urlSite ?? '',
      welcomeMessage: response.mensagem ?? '',
      emailSubject: response.emailAssunto ?? '',
      emailBody: response.emailCorpo ?? '',
      anexarInstrucoes: response.isAnexarInstrucoesFaturamento ?? true,
      emailResposta: response.emailResposta ?? '',
      instrucoesTexto: response.textoInstrucoesFaturamento ?? '',
    });

    // A versão de fábrica é do backend: a tela só a guarda para os "Restaurar texto padrão".
    this.textoInstrucoesPadrao = response.textoInstrucoesFaturamentoPadrao ?? '';
    this.emailAssuntoPadrao = response.emailAssuntoPadrao ?? '';
    this.emailCorpoPadrao = response.emailCorpoPadrao ?? '';

    const formatosSelecionados = new Set(response.formatos ?? []);
    this.formatOptions.forEach(format =>
      this.form.get(format.key)?.setValue(formatosSelecionados.has(format.id)));

    const chavesCampos = new Set((response.campos ?? []).map(c => c.chave));
    this.documentFields
      .filter(field => !field.fixo)
      .forEach(field => this.form.get(field.key)?.setValue(chavesCampos.has(field.key)));

    this.form.markAsPristine();
  }

  public ngOnDestroy(): void {
    this.editorEmail.destroy();
    this.editorInstrucoes.destroy();
  }

  // Razão social / CNPJ / endereço são dados de cadastro da empresa (db_usuario):
  // exibidos somente leitura aqui.
  private carregarEmpresa(): void {
    const idEmpresa = this.authService.getUserInfos()?.idEmpresa;
    if (!idEmpresa) {
      return;
    }
    this.getEmpresaService.obterPorId(idEmpresa).subscribe({
      next: response => {
        if (response.isError || !response.empresa) {
          return;
        }
        const empresa = response.empresa;
        this.form.patchValue({
          brandName: empresa.nomeEmpresa ?? '',
          buyerCnpj: empresa.cnpj ?? '',
          buyerAddress: this.montarEndereco(empresa),
        });
      },
      error: () => { /* dados da empresa são complementares; falha silenciosa */ },
    });
  }

  private montarEndereco(empresa: EmpresaStruct): string {
    const numero = [empresa.logradouro, empresa.numero].filter(Boolean).join(', ');
    const cidadeUf = [empresa.municipio, empresa.uf].filter(Boolean).join('/');
    const partes = [numero, empresa.complemento, empresa.bairro, cidadeUf, empresa.cep ? `CEP ${empresa.cep}` : null];
    return partes.filter(Boolean).join(' - ');
  }

  public voltar(): void {
    this.router.navigate(['/configuracoes/suprimentos']);
  }

  public salvar(): void {
    if (!this.configCarregada) {
      this.alertService.show('Atenção', 'A configuração atual não pôde ser carregada. Recarregue a página antes de salvar.', AlertType.warning);
      return;
    }

    this.isSaving = true;
    const request = this.montarRequest();
    this.configuracaoPedidoService.salvar(request).subscribe({
      next: response => {
        this.isSaving = false;
        if (response.isError) {
          this.alertService.show('Atenção', response.errorDescription || 'Não foi possível salvar a configuração.', AlertType.warning);
          return;
        }
        this.form.markAsPristine();
        this.alertService.show('Sucesso', 'Configuração salva com sucesso.', AlertType.success);
      },
      error: () => {
        this.isSaving = false;
        this.alertService.show('Erro', 'Não foi possível salvar a configuração.', AlertType.error);
      },
    });
  }

  // Monta o payload achatando os toggles de formato/seção de volta para listas.
  private montarRequest(): ConfiguracaoPedidoRequest {
    const v = this.form.getRawValue();
    const request = new ConfiguracaoPedidoRequest();

    request.marcaSigla = v.brandShort || null;
    request.marcaRodape = v.brandTagline || null;
    request.urlLogo = this.logoDataUrl;
    request.corMarca = v.brandColor || '#B73232';
    request.corHeroInicio = v.heroBg || '#1C1B1F';
    request.corHeroFim = v.heroBgEnd || '#2D2A3A';
    request.emailContato = v.buyerEmail || null;
    request.telefoneContato = v.buyerPhone || null;
    request.urlSite = v.portalSubdomain || null;
    request.mensagem = v.welcomeMessage || null;
    request.emailAssunto = v.emailSubject || null;
    request.emailCorpo = v.emailBody || null;
    request.isAnexarInstrucoesFaturamento = !!v.anexarInstrucoes;
    request.emailResposta = v.emailResposta || null;
    request.textoInstrucoesFaturamento = v.instrucoesTexto || null;

    request.formatos = this.formatOptions
      .filter(format => !!this.form.get(format.key)?.value)
      .map(format => format.id);

    // Só as seções ativáveis (não fixas) que estão ligadas.
    request.campos = this.documentFields
      .filter(field => !field.fixo && !!this.form.get(field.key)?.value)
      .map(field => ({
        chave: field.key,
        idTipoCampo: field.idTipoCampo ?? TipoCampoEnum.Texto,
        nome: field.name,
        isObrigatorio: false,
      }));

    return request;
  }

  public toggleVariaveisEmail(): void {
    this.showVariaveisEmail = !this.showVariaveisEmail;
  }

  public toggleVariaveisInstrucoes(): void {
    this.showVariaveisInstrucoes = !this.showVariaveisInstrucoes;
  }

  public onColorChange(event: Event, controlName: string): void {
    const input = event.target as HTMLInputElement;
    const color = input.value;
    if (color && color.startsWith('#') && (color.length === 4 || color.length === 7)) {
      this.form.get(controlName)?.setValue(color, { emitEvent: false });
      this.form.markAsDirty();
    }
  }

  public applyPreset(preset: PresetColor): void {
    this.form.patchValue({
      brandColor: preset.color,
      heroBg: preset.heroBg,
      heroBgEnd: preset.heroBgEnd,
    });
    this.form.markAsDirty();
  }

  public onLogoSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = () => {
      this.logoDataUrl = String(reader.result);
      this.form.markAsDirty();
    };
    reader.readAsDataURL(file);
  }

  public removeLogo(): void {
    this.logoDataUrl = null;
    this.form.markAsDirty();
  }

  public insertVariable(variable: string): void {
    this.editorEmail.commands.insertText(variable).focus().exec();
    this.form.get('emailBody')?.markAsDirty();
  }

  public insertVariableInstrucoes(variavel: string): void {
    this.editorInstrucoes.commands.insertText(variavel).focus().exec();
    this.form.get('instrucoesTexto')?.markAsDirty();
  }

  // Repõe a versão de fábrica do assunto e da mensagem — os dois compõem o template de e-mail.
  // Sem a versão de fábrica em mãos, não faz nada: apagar o texto do usuário seria pior.
  public restaurarTemplateEmail(): void {
    if (!this.emailAssuntoPadrao && !this.emailCorpoPadrao) {
      this.alertService.show('Atenção', 'Não foi possível obter o texto padrão. Recarregue a página e tente novamente.', AlertType.warning);
      return;
    }

    this.form.get('emailSubject')?.setValue(this.emailAssuntoPadrao);
    this.form.get('emailBody')?.setValue(this.emailCorpoPadrao);
    this.form.get('emailSubject')?.markAsDirty();
    this.form.get('emailBody')?.markAsDirty();
  }

  public restaurarInstrucoes(): void {
    if (!this.textoInstrucoesPadrao) {
      this.alertService.show('Atenção', 'Não foi possível obter o texto padrão. Recarregue a página e tente novamente.', AlertType.warning);
      return;
    }

    this.form.get('instrucoesTexto')?.setValue(this.textoInstrucoesPadrao);
    this.form.get('instrucoesTexto')?.markAsDirty();
  }

  public isFormatSelected(formatKey: string): boolean {
    return !!this.form.get(formatKey)?.value;
  }

  public toggleFormat(formatKey: string): void {
    const control = this.form.get(formatKey);
    if (!control) {
      return;
    }

    control.setValue(!control.value);
    control.markAsDirty();
  }

  public isFieldActive(field: DocumentField): boolean {
    return field.fixo || !!this.form.get(field.key)?.value;
  }

  public get hasSelectedFormat(): boolean {
    return this.formatOptions.some(format => !!this.form.get(format.key)?.value);
  }

  private createForm(): FormGroup {
    return this.formBuilder.group({
      brandShort: ['ATS'],
      brandTagline: ['Portal do Fornecedor'],
      brandColor: ['#B73232'],
      heroBg: ['#1C1B1F'],
      heroBgEnd: ['#2D2A3A'],
      // Somente leitura: preenchidos a partir do cadastro da empresa (db_usuario).
      brandName: [{ value: '', disabled: true }],
      buyerCnpj: [{ value: '', disabled: true }],
      buyerAddress: [{ value: '', disabled: true }],
      buyerEmail: [''],
      buyerPhone: [''],
      portalSubdomain: [''],
      welcomeMessage: ['Confira os itens, o prazo de entrega e as condições de pagamento do pedido.'],
      formatoLinkPortal: [true],
      formatoPlanilhaExcel: [false],
      formatoPdfPreencher: [true],
      campoCondicoesPagamento: [true],
      campoPrazoEntrega: [true],
      campoObservacoes: [true],
      // Assunto, corpo e instruções chegam do backend: é lá que o texto da empresa fica gravado.
      emailSubject: [''],
      emailBody: [''],
      anexarInstrucoes: [true],
      emailResposta: [''],
      instrucoesTexto: [''],
    });
  }
}
